import React, { useId } from "react";
import { useTranslation } from "react-i18next";
import { footerPhrase } from "./wordText";

const CODE_LIMIT = 16;
// Control, invisible (format), private-use and unassigned characters: the server refuses them in the code.
const INVISIBLE = /\p{C}/u;

const labelClass = "mb-1.5 block text-sm font-medium text-slate-700";
// The exam forms' field look; 44px on phones.
const fieldClass =
  "block h-10 w-full rounded-lg border text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 hover:border-slate-400 focus:ring-4 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500 read-only:bg-slate-50 read-only:text-slate-600 max-sm:h-11";
const okClass = "border-slate-300 focus:border-blue-600 focus:ring-blue-600/15";
const errorClass = "border-red-300 focus:border-red-500 focus:ring-red-500/15";

// The two fields as the API takes them: "" is null (no language: the Word prints Russian; no code: nothing in the
// footer's right). Sent with every save of the form; a save without them would keep them.
export const wordPrintPayload = ({ language, print_code: code }) => ({
  language: language || null,
  print_code: (code || "").trim() || null,
});

// The code's problem as the client sees it, or "": the server checks the same (and its field errors are shown too).
export const printCodeProblem = (t, code) => {
  const value = (code || "").trim();
  if (INVISIBLE.test(value)) return t("exam_bank.form.print_code_invalid");
  if ([...value].length > CODE_LIMIT) return t("exam_bank.form.print_code_too_long");
  return "";
};

// A field's server errors ({print_code: ["…"]}) as one line.
export const fieldErrorText = (messages) =>
  Array.isArray(messages) ? messages.filter((m) => typeof m === "string").join(" ") : typeof messages === "string" ? messages : "";

/**
 * The exam's Word settings in its create and edit forms: the language of the Word's fixed labels (kk, ru, or none,
 * which prints Russian) and the variant code printed on the right of its footer («НҰСҚА – 3»). Each has a one-line
 * hint (the code's says live what the footer will read) and its error under it, tied to the field. `onChange(field,
 * value)` with field "language" or "print_code"; `errors` = {language, print_code} texts; `readOnly`: an archived
 * exam (the select is disabled, the code read-only).
 */
export default function WordPrintFields({
  idPrefix,
  language,
  printCode,
  onChange,
  errors = {},
  disabled = false,
  readOnly = false,
  languageRef,
  codeRef,
  className = "",
}) {
  const [t] = useTranslation();
  const uid = useId();
  const base = idPrefix || `word-print-${uid}`;
  const ids = {
    language: `${base}-language`,
    languageHint: `${base}-language-hint`,
    languageError: `${base}-language-error`,
    code: `${base}-print-code`,
    codeHint: `${base}-print-code-hint`,
    codeError: `${base}-print-code-error`,
  };
  const code = (printCode || "").trim();
  const describedBy = (hint, error, message) => [hint, message ? error : null].filter(Boolean).join(" ");

  // The line above it is the wrapper's: a fieldset's border would run through its legend.
  return (
    <div className={className}>
      <fieldset>
        <legend className="mb-3 text-sm font-semibold text-slate-900">{t("exam_bank.word.title")}</legend>
        <div className="space-y-4">
          <div>
            <label htmlFor={ids.language} className={labelClass}>
              {t("exam_bank.form.language")}
            </label>
            <select
              ref={languageRef}
              id={ids.language}
              value={language || ""}
              onChange={(e) => onChange("language", e.target.value)}
              disabled={disabled || readOnly}
              aria-invalid={errors.language ? true : undefined}
              aria-describedby={describedBy(ids.languageHint, ids.languageError, errors.language)}
              className={`${fieldClass} ${errors.language ? errorClass : okClass} pl-3 pr-9`}
            >
              <option value="kk">{t("exam_bank.word.language_kk")}</option>
              <option value="ru">{t("exam_bank.word.language_ru")}</option>
              <option value="">{t("exam_bank.form.language_none")}</option>
            </select>
            <p id={ids.languageHint} className="mt-1.5 text-xs text-slate-500">
              {t("exam_bank.form.language_hint")}
            </p>
            {errors.language && (
              <p id={ids.languageError} className="mt-1.5 text-sm text-red-600">
                {errors.language}
              </p>
            )}
          </div>

          <div>
            <label htmlFor={ids.code} className={labelClass}>
              {t("exam_bank.form.print_code")}
            </label>
            <input
              ref={codeRef}
              id={ids.code}
              type="text"
              value={printCode || ""}
              onChange={(e) => onChange("print_code", e.target.value)}
              maxLength={CODE_LIMIT}
              autoComplete="off"
              spellCheck={false}
              disabled={disabled}
              readOnly={readOnly}
              placeholder={t("exam_bank.form.print_code_placeholder")}
              aria-invalid={errors.print_code ? true : undefined}
              aria-describedby={describedBy(ids.codeHint, ids.codeError, errors.print_code)}
              className={`${fieldClass} ${errors.print_code ? errorClass : okClass} px-3 sm:w-56`}
            />
            <p id={ids.codeHint} className="mt-1.5 text-xs text-slate-500">
              {code
                ? t("exam_bank.form.print_code_hint", { phrase: footerPhrase(language, code) })
                : t("exam_bank.form.print_code_hint_empty")}
            </p>
            {errors.print_code && (
              <p id={ids.codeError} className="mt-1.5 text-sm text-red-600">
                {errors.print_code}
              </p>
            )}
          </div>
        </div>
      </fieldset>
    </div>
  );
}
